import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Briefcase, Users, Wallet, LogOut, Loader2,
  ClipboardList, CheckCircle2, UserCheck, Hourglass, BadgeCheck,
  MapPin, IndianRupee, Clock, Send, Search,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import FreelancerSubmitDialog from "@/components/freelancer/FreelancerSubmitDialog";
import { fetchFreelancerJobs } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "jobs", label: "Assigned Jobs", icon: Briefcase },
  { key: "candidates", label: "My Candidates", icon: Users },
  { key: "commission", label: "Commission", icon: Wallet },
];

export default function FreelancerDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo variant="light" to="/" />
            <span className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-[10px] uppercase tracking-wider font-bold">
              Freelancer
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right text-xs">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="text-slate-500">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="freelancer-logout"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:py-8 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-20 self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                data-testid={`freelancer-tab-${key}`}
                className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  active === key
                    ? "bg-primary text-white shadow-sm"
                    : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="min-w-0">
          {active === "overview" && <Overview />}
          {active === "jobs" && <AssignedJobs />}
          {active === "candidates" && <MyCandidates />}
          {active === "commission" && <Commission />}
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/freelancer/stats`)
      .then((r) => setStats(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) return <ErrorBlock msg={err} />;
  if (!stats) return <Loading />;

  const cards = [
    { key: "assigned_jobs", label: "Assigned Jobs", value: stats.assigned_jobs, icon: Briefcase, cls: "text-primary bg-primary/10" },
    { key: "submitted_candidates", label: "Submitted Candidates", value: stats.submitted_candidates, icon: ClipboardList, cls: "text-blue-600 bg-blue-50" },
    { key: "selected_candidates", label: "Selected Candidates", value: stats.selected_candidates, icon: BadgeCheck, cls: "text-amber-600 bg-amber-50" },
    { key: "joined_candidates", label: "Joined Candidates", value: stats.joined_candidates, icon: UserCheck, cls: "text-emerald-600 bg-emerald-50" },
    { key: "pending_commission", label: "Pending Commission", value: stats.pending_commission, icon: Hourglass, cls: "text-orange-600 bg-orange-50" },
    { key: "paid_commission", label: "Paid Commission", value: stats.paid_commission, icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50" },
  ];

  return (
    <div className="space-y-6" data-testid="freelancer-overview">
      <PageHeader title="Dashboard" subtitle="Your assigned jobs and submissions at a glance." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(({ key, label, value, icon: Icon, cls }) => (
          <div key={key} data-testid={`freelancer-stat-${key}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cls}`}>
                <Icon className="w-4 h-4" strokeWidth={1.6} />
              </div>
            </div>
            <div className="mt-3 font-heading font-extrabold text-3xl text-slate-900">{value}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-slate-900">What's next</h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          Head to <span className="font-semibold text-slate-900">Assigned Jobs</span> to view openings and submit candidates,
          or check <span className="font-semibold text-slate-900">My Candidates</span> for the current status of your submissions.
        </p>
      </div>
    </div>
  );
}

function AssignedJobs() {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => {
    setJobs(null);
    fetchFreelancerJobs().then(setJobs).catch(() => setJobs([]));
  };
  useEffect(() => { load(); }, []);

  const fmtINR = (n) =>
    typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";

  const filtered = useMemo(() => {
    if (!jobs) return [];
    const s = q.trim().toLowerCase();
    if (!s) return jobs;
    return jobs.filter((j) =>
      [j.title, j.company_name, j.location, j.department, j.experience].join(" ").toLowerCase().includes(s)
    );
  }, [jobs, q]);

  return (
    <div data-testid="freelancer-assigned-jobs">
      <PageHeader title="Assigned Jobs" subtitle="Openings assigned to you. Submit candidates from here." />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by title, company, location…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="freelancer-jobs-search"
              className="pl-9 h-10"
            />
          </div>
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {jobs === null ? "" : `${filtered.length} ${filtered.length === 1 ? "job" : "jobs"}`}
          </div>
        </div>

        <div className="overflow-x-auto">
          {jobs === null ? (
            <Loading />
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500 px-6">
              {jobs.length === 0
                ? "No jobs have been assigned to you yet. Please check back later."
                : "No jobs match your search."}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <Th>Job</Th><Th>Location</Th><Th>Salary</Th><Th>Experience</Th><Th>Openings</Th><Th>Commission</Th><Th className="text-right">Action</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((j) => (
                  <tr key={j.id} data-testid={`freelancer-job-row-${j.id}`} className="hover:bg-slate-50">
                    <Td>
                      <div className="font-semibold text-slate-900">{j.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {j.company_name || "—"} · <span className="text-primary font-semibold uppercase tracking-wider">{j.department}</span>
                      </div>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 text-slate-700"><MapPin className="w-3.5 h-3.5 text-slate-400" />{j.location}</span>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1 text-slate-700"><IndianRupee className="w-3.5 h-3.5 text-slate-400" />{j.salary_range || "Negotiable"}</span>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 text-slate-700"><Clock className="w-3.5 h-3.5 text-slate-400" />{j.experience}</span>
                    </Td>
                    <Td>
                      <span className="font-semibold text-slate-900">{j.openings}</span>
                    </Td>
                    <Td>
                      <span
                        data-testid={`freelancer-job-commission-${j.id}`}
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-bold border ${
                          j.commission_amount
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                            : "bg-slate-50 text-slate-500 border-slate-200"
                        }`}
                      >
                        {fmtINR(j.commission_amount)}
                      </span>
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        onClick={() => { setTarget(j); setDialogOpen(true); }}
                        data-testid={`freelancer-submit-btn-${j.id}`}
                        className="gap-1.5 bg-primary hover:bg-primary-hover text-white"
                      >
                        <Send className="w-3.5 h-3.5" /> Submit Candidate
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <FreelancerSubmitDialog
        job={target}
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setTarget(null); }}
        onSubmitted={load}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-xs font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

// ---------- Status pill ----------
const STATUS_STYLES = {
  submitted:   "bg-slate-100 text-slate-700 border-slate-200",
  shortlisted: "bg-blue-50 text-blue-700 border-blue-100",
  interview:   "bg-indigo-50 text-indigo-700 border-indigo-100",
  selected:    "bg-amber-50 text-amber-700 border-amber-100",
  joined:      "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected:    "bg-red-50 text-red-600 border-red-100",
};
function StatusPill({ value }) {
  const key = (value || "submitted").toLowerCase();
  const cls = STATUS_STYLES[key] || STATUS_STYLES.submitted;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider border ${cls}`}>
      {key}
    </span>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = typeof s === "string" ? new Date(s) : s;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(s); }
}

function fmtINR(n) {
  return typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";
}

// ---------- My Candidates ----------
function MyCandidates() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/freelancer/candidates`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.full_name, it.email, it.phone, it.job_title, it.status].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  return (
    <div data-testid="freelancer-my-candidates">
      <PageHeader title="My Candidates" subtitle="Candidates you have submitted and their current status." />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, email, job or status…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="freelancer-candidates-search"
              className="pl-9 h-10"
            />
          </div>
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {items === null ? "" : `${filtered.length} ${filtered.length === 1 ? "candidate" : "candidates"}`}
          </div>
        </div>
        <div className="overflow-x-auto">
          {items === null ? <Loading /> : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              {items.length === 0 ? "You have not submitted any candidates yet." : "No matches."}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <Th>Candidate</Th><Th>Job</Th><Th>Submitted</Th><Th>Contact</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`freelancer-candidate-row-${c.id}`} className="hover:bg-slate-50">
                    <Td>
                      <div className="font-semibold text-slate-900">{c.full_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono select-all break-all">{c.id}</div>
                    </Td>
                    <Td className="max-w-[240px]">
                      <div className="text-slate-900 truncate" title={c.job_title}>{c.job_title || "—"}</div>
                    </Td>
                    <Td className="text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</Td>
                    <Td className="text-slate-700 whitespace-nowrap">
                      <div className="text-xs">{c.email}</div>
                      <div className="text-xs text-slate-500">{c.phone}</div>
                    </Td>
                    <Td data-testid={`freelancer-candidate-status-${c.id}`}>
                      <StatusPill value={c.status} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Commission ----------
function Commission() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/freelancer/commissions`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.candidate_name, it.job_title, it.status, it.commission_status].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const totals = useMemo(() => {
    let pending = 0, paid = 0;
    (items || []).forEach((it) => {
      const amt = typeof it.commission_amount === "number" ? it.commission_amount : 0;
      if (it.commission_status === "paid") paid += amt; else pending += amt;
    });
    return { pending, paid };
  }, [items]);

  return (
    <div data-testid="freelancer-commission">
      <PageHeader title="Commission" subtitle="Your commission per candidate. Updated by the admin team." />
      {items && items.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Pending</div>
            <div className="mt-2 font-heading font-extrabold text-2xl text-orange-600">{fmtINR(totals.pending)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Paid</div>
            <div className="mt-2 font-heading font-extrabold text-2xl text-emerald-600">{fmtINR(totals.paid)}</div>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search candidate, job or status…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="freelancer-commission-search"
              className="pl-9 h-10"
            />
          </div>
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {items === null ? "" : `${filtered.length} ${filtered.length === 1 ? "entry" : "entries"}`}
          </div>
        </div>
        <div className="overflow-x-auto">
          {items === null ? <Loading /> : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              {(items && items.length === 0)
                ? "No commissions yet. Selected or joined candidates will appear here."
                : "No matches."}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <Th>Candidate</Th><Th>Job</Th><Th>Application Status</Th><Th>Commission Amount</Th><Th>Payment</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.application_id} data-testid={`freelancer-commission-row-${c.application_id}`} className="hover:bg-slate-50">
                    <Td className="font-medium text-slate-900">{c.candidate_name}</Td>
                    <Td className="max-w-[220px] truncate" title={c.job_title}>{c.job_title || "—"}</Td>
                    <Td><StatusPill value={c.status} /></Td>
                    <Td className="font-semibold text-slate-900 whitespace-nowrap">{fmtINR(c.commission_amount)}</Td>
                    <Td data-testid={`freelancer-commission-status-${c.application_id}`}>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${
                        c.commission_status === "paid"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                          : "bg-orange-50 text-orange-700 border-orange-100"
                      }`}>
                        {c.commission_status || "pending"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ComingSoon({ title, note }) {  return (
    <div data-testid="freelancer-coming-soon">
      <PageHeader title={title} subtitle={note} />
      <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
        <div className="inline-flex w-12 h-12 rounded-2xl bg-primary/10 items-center justify-center mb-4">
          <Loader2 className="w-5 h-5 text-primary" />
        </div>
        <p className="font-heading font-bold text-slate-900">Coming next</p>
        <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">{note}</p>
      </div>
    </div>
  );
}

function PageHeader({ title, subtitle }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

function Loading() {
  return <div className="py-16 text-center text-slate-500 inline-flex items-center justify-center w-full gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
}
function ErrorBlock({ msg }) {
  return <div className="p-6 rounded-2xl border border-destructive/30 bg-destructive/10 text-destructive text-sm">{msg}</div>;
}
